import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { css } from '@emotion/css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Image,
  List,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  PhaseType,
  ProjectProposalPage,
  ProjectProvider,
  ProjectSitePlanPage,
  useProject,
} from '../../../modules/project';
import type { IPhase } from '../../../modules/project';
import { Loader } from '../../../components/UI/Loader';
import { queries } from '../../../graphql/graphql';
import type { MapPhaseData } from '../../../modules/project/models/MapPhaseModel';
import type { IPlan } from '../../../types/plans';

const { Text, Title } = Typography;

const siteVisitFrameClass = css`
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  & [style*='width: 100vw'] {
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const formatDate = (value?: string | number) => {
  if (value === null || value === undefined || value === '') return '—';
  const date =
    typeof value === 'number'
      ? new Date(value)
      : (() => {
          const trimmed = value.trim();
          const numeric = Number(trimmed);
          if (!Number.isNaN(numeric) && trimmed !== '') {
            return new Date(numeric);
          }
          return new Date(value);
        })();
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatPhaseType = (type?: string) => {
  switch (type) {
    case PhaseType.MAP:
      return 'Map';
    case PhaseType.SITE_PLAN:
      return 'Site plan';
    case PhaseType.PROPOSAL:
      return 'Proposal';
    default:
      return 'Phase';
  }
};

const getStatusColor = (status?: string) => {
  if (!status) return 'default';
  if (status.toLowerCase() === 'active') return 'green';
  if (status.toLowerCase() === 'draft') return 'gold';
  return 'blue';
};

const getPlanImage = (plan?: IPlan | null) => {
  if (!plan) return null;
  if (plan.image) return plan.image;
  const primaryPhoto = plan.photos?.find(photo => photo.isPrimary) || plan.photos?.[0];
  return primaryPhoto?.original || primaryPhoto?.compressed?.url || null;
};

const ProjectSetupSummary: React.FC<{ phase: IPhase }> = ({ phase }) => {
  const data = (phase.data || {}) as MapPhaseData;
  const address = data.address || 'Address not set';
  const planId = data.planId;

  const { data: planData, loading } = useQuery(queries.plans.PLAN, {
    variables: { id: planId },
    skip: !planId,
    fetchPolicy: 'network-only',
  });

  const plan = planData?.plan as IPlan | undefined;
  const planImage = getPlanImage(plan);
  const resolvedPlanId = plan?.id || planId;

  return (
    <Card title="Project Setup">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Address">{address}</Descriptions.Item>
        </Descriptions>
        <div>
          <Text strong>Selected plan</Text>
          <div style={{ marginTop: 8 }}>
            {!planId ? (
              <Text type="secondary">No plan selected yet.</Text>
            ) : loading ? (
              <Spin />
            ) : plan ? (
              <Card size="small">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Text strong>{plan.title || 'Untitled plan'}</Text>
                  {planImage && (
                    <Image
                      src={planImage}
                      alt={plan.title || 'Plan'}
                      style={{ width: '100%', maxHeight: 320, objectFit: 'contain' }}
                      preview={false}
                    />
                  )}
                  {resolvedPlanId && (
                    <Space>
                      <Button onClick={() => window.open(`/plan/${resolvedPlanId}`, '_blank')}>
                        Open plan
                      </Button>
                      <Button
                        onClick={() => window.open(`/admin/plan/${resolvedPlanId}`, '_blank')}>
                        Admin view
                      </Button>
                    </Space>
                  )}
                </Space>
              </Card>
            ) : (
              <Text type="secondary">Plan not found.</Text>
            )}
          </div>
        </div>
      </Space>
    </Card>
  );
};

const ProjectPhaseContent: React.FC<{ phase: IPhase | null }> = ({ phase }) => {
  if (!phase) {
    return <Empty description="No phases available for this project." />;
  }

  switch (phase.type) {
    case PhaseType.MAP:
      return <ProjectSetupSummary phase={phase} />;
    case PhaseType.SITE_PLAN:
      return (
        <div className={siteVisitFrameClass}>
          <ProjectSitePlanPage phaseId={phase.id} />
        </div>
      );
    case PhaseType.PROPOSAL:
      return <ProjectProposalPage phaseId={phase.id} />;
    default:
      return <Empty description="Phase view is not implemented yet." />;
  }
};

const ProjectPhaseSidebar: React.FC<{
  phases: IPhase[];
  loading: boolean;
  selectedPhaseId: string | null;
  actualActivePhaseId: string | null;
  onSelectPhase: (phaseId: string) => void;
}> = ({ phases, loading, selectedPhaseId, actualActivePhaseId, onSelectPhase }) => {

  const orderedPhases = useMemo(() => {
    return [...phases].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  }, [phases]);

  if (loading) {
    return <Loader />;
  }

  if (!orderedPhases.length) {
    return <Empty description="No phases" />;
  }

  const renderPhase = (phase: IPhase) => {
    const isSelected = phase.id === selectedPhaseId;
    const isActualActive = phase.id === actualActivePhaseId;
    return (
      <List.Item
        onClick={() => onSelectPhase(phase.id)}
        style={{
          cursor: 'pointer',
          padding: '12px 16px',
          borderLeft: isSelected ? '3px solid #1677ff' : '3px solid transparent',
          backgroundColor: isSelected ? '#fafafa' : 'transparent',
        }}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space size={8} wrap>
            <Text strong={isSelected}>{phase.name}</Text>
            {phase.locked && <Tag>Locked</Tag>}
            {isActualActive && <Tag color="blue">Active</Tag>}
            {typeof phase.progress === 'number' && (
              <Tag color="default">{Math.round(phase.progress)}%</Tag>
            )}
          </Space>
          <Text type="secondary">{formatPhaseType(phase.type)}</Text>
        </Space>
      </List.Item>
    );
  };

  return <List dataSource={orderedPhases} renderItem={renderPhase} />;
};

const ProjectViewContent: React.FC = () => {
  const navigate = useNavigate();
  const { project, phases, loading } = useProject();
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);

  const ownerLabel =
    project?.owner?.name || project?.owner?.email || project?.ownerId || '—';

  const actualActivePhaseId = useMemo(() => {
    return phases.find(phase => phase.isActive)?.id || phases[0]?.id || null;
  }, [phases]);

  useEffect(() => {
    if (!selectedPhaseId || !phases.some(phase => phase.id === selectedPhaseId)) {
      setSelectedPhaseId(actualActivePhaseId);
    }
  }, [actualActivePhaseId, phases, selectedPhaseId]);

  const selectedPhase = useMemo(() => {
    if (!selectedPhaseId) return null;
    return phases.find(phase => phase.id === selectedPhaseId) || null;
  }, [phases, selectedPhaseId]);

  if (loading) {
    return <Loader />;
  }

  if (!project) {
    return (
      <Card>
        <Empty description="Project not found" />
        <Button type="primary" onClick={() => navigate('/admin/projects')}>
          Back to Projects
        </Button>
      </Card>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card
          title={<Title level={4} style={{ margin: 0 }}>{project.name}</Title>}
          extra={
            <Button onClick={() => navigate('/admin/projects')}>Back to Projects</Button>
          }>
          <Space direction="vertical" size={8}>
            <Space size={8} wrap>
              {project.status && (
                <Tag color={getStatusColor(project.status)}>
                  {project.status.toUpperCase()}
                </Tag>
              )}
              <Tag color="default">Phases: {project.phases?.length || 0}</Tag>
              <Tag color="default">Users: {project.users?.length || 0}</Tag>
            </Space>
            <Text type="secondary">
              Owner:{' '}
              {project.owner?.id ? (
                <Link to={`/admin/user/${project.owner.id}`}>{ownerLabel}</Link>
              ) : (
                ownerLabel
              )}
            </Text>
            <Text type="secondary">Created: {formatDate(project.createdAt)}</Text>
            <Text type="secondary">Updated: {formatDate(project.updatedAt)}</Text>
          </Space>
        </Card>
      </Col>
      <Col xs={24} md={7} lg={6}>
        <Card title="Phases">
          <ProjectPhaseSidebar
            phases={phases}
            loading={loading}
            selectedPhaseId={selectedPhaseId}
            actualActivePhaseId={actualActivePhaseId}
            onSelectPhase={setSelectedPhaseId}
          />
        </Card>
      </Col>
      <Col xs={24} md={17} lg={18}>
        <ProjectPhaseContent phase={selectedPhase} />
      </Col>
    </Row>
  );
};

const AdminProjectView: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <Empty description="Project ID not provided." />;
  }

  return (
    <ProjectProvider projectId={id}>
      <ProjectViewContent />
    </ProjectProvider>
  );
};

export default AdminProjectView;
